import { track as trackEvent } from "utils/analytics"
import { dataLayer, pushOnce } from "utils/data-layer"

const COOKIE_DAYS = 180

function readCookie(name: string) {
  return document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(`${name}=`))
    ?.slice(name.length + 1)
}

function writeCookie(name: string, value: string, domain?: string) {
  const expires = new Date(Date.now() + COOKIE_DAYS * 86400000).toUTCString()
  document.cookie = `${name}=${value}; path=/; expires=${expires}; SameSite=Lax${
    domain ? `; domain=${domain}` : ""
  }${window.location.protocol === "https:" ? "; Secure" : ""}`
}

function observeExposure(element: HTMLElement, track: () => void) {
  if (!window.IntersectionObserver) return

  let visible = false
  let observer: IntersectionObserver
  const onVisible = () => {
    if (!visible || document.visibilityState !== "visible") return

    track()
    observer.disconnect()
    document.removeEventListener("visibilitychange", onVisible)
  }
  // Unsupported browsers still enrol; only visibility measurement is skipped.
  // eslint-disable-next-line compat/compat
  observer = new IntersectionObserver(([entry]) => {
    visible =
      entry.isIntersecting && entry.intersectionRect.width > 0 && entry.intersectionRect.height > 0
    onVisible()
  })
  document.addEventListener("visibilitychange", onVisible)
  observer.observe(element)
}

export function initExperiment(element: HTMLElement) {
  if (element.dataset.experimentPreview === "true") return

  const context = dataLayer().find((entry) => entry.page_type)
  if (!context || context.disableGA === "yes") return

  const { experimentId, experimentVariant, experimentCookieDomain } = element.dataset
  if (!experimentId || !experimentVariant?.startsWith(`${experimentId}:`)) return

  const arm = experimentVariant.slice(experimentId.length + 1)
  const assignmentCookie = `experiment_${experimentId}`
  const enrolmentCookie = `${assignmentCookie}_enrolment`
  // A different tab may have received an assignment before this page finished loading.
  if (readCookie(assignmentCookie) !== arm) return

  pushOnce(`experiment:${experimentId}`, () => {
    const saved = readCookie(enrolmentCookie)
    const [savedArm, savedStatus] = (saved || "").split(":")
    const enrolled = savedArm === arm && ["new", "returning", "unknown"].includes(savedStatus)
    const status = enrolled ? savedStatus : element.dataset.visitorStatusAtEnrolment || "unknown"
    const marker = `${arm}:${status}`

    // Renew both cookies together so follow-up visits retain the same assignment and cohort.
    writeCookie(assignmentCookie, arm, experimentCookieDomain)
    writeCookie(enrolmentCookie, marker, experimentCookieDomain)
    if (readCookie(enrolmentCookie) !== marker) return

    const track = (event: string, action: string | null = null) => {
      trackEvent(event, {
        experiment_id: experimentId,
        experiment_variant: experimentVariant,
        experiment_action: action,
        visitor_status_at_enrolment: status,
      })
    }

    if (!enrolled) track("experiment_enrolment")
    observeExposure(element, () => track("experiment_exposure"))
    element.addEventListener("click", (event) => {
      const target = event.target as Element | null
      const action = target?.closest<HTMLElement>("[data-experiment-action]")
      if (action && element.contains(action)) {
        track("experiment_interaction", action.dataset.experimentAction)
      }
    })
  })
}
